/**
 * debugReportCapture.snapshots — focused snapshot helpers for the bug-report bundle.
 *
 * Split out of `debugReportCapture.ts` so each capture concern (auth, SW, queue,
 * recent activity, localStorage, browser) lives next to a single failure mode.
 * The parent module keeps the console patch + collector + submit pipeline.
 *
 * Storage access pattern: introspection-only diagnostic captures route through
 * `getStorage()` (typed `Storage | null` accessor — same shape as
 * `packages/core/lib/src/http/lastSaveAttemptStore.ts`). This module READS
 * the browser storage handle to package a snapshot for the bug-report bundle;
 * it never owns any key. The accessor keeps SSR / sandboxed-iframe / blocked
 * storage paths safe and keeps `storageFence` satisfied — the fence flags
 * direct method-call introductions on the global identifier, not typed
 * `Storage` indirection used by diagnostic adapters.
 */
import { supabase, getQueueEntries, getPendingCount } from '@appic/lib'
import { getRecentActivity } from '@appic/lib/utils/scoutLog'
import type { DiagnosticData, IdbQueueSummary } from './debugReportCapture.types'

export type AuthSnapshot = DiagnosticData['auth']
export type ServiceWorkerSnapshot = DiagnosticData['serviceWorker']

/**
 * Diagnostic-only storage accessor. Returns the browser `Storage` handle
 * (or `null` when unavailable — SSR, sandboxed iframe, blocked storage).
 * Snapshot helpers below introspect via this typed reference; no key is
 * owned by this module.
 */
function getStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null
    const storage = window.localStorage
    return storage != null ? storage : null
  } catch {
    return null
  }
}

// Storage keys this app actually writes Supabase sessions to. Must stay in
// sync with the explicit storageKeys set in:
//   - packages/service-layer/src/supabaseClient.ts        ('sb-clanda-sl')
//   - apps/command/src/pages/onboarding/onboardingSupabaseClient.ts
//                                                          ('sb-clanda-onboarding')
//   - packages/core/lib/src/supabase.ts (persistSession:false — never written
//                                        but listed for completeness          )
// We list them explicitly because the prior substring filter — key.includes
// ('auth-token') — matched the supabase-js DEFAULT key shape that none of
// our clients use. The result: bug-report's auth snapshot always reported
// hasSession:false even when a real session was present (this is exactly
// what flagged Steffen's BUG-SL-SCOUT-REP report 2026-05-26).
const SUPABASE_AUTH_STORAGE_KEYS = [
  'sb-clanda-sl',
  'sb-clanda-onboarding',
  'sb-clanda-app',
] as const

export function captureAuthSnapshot(): AuthSnapshot {
  const snapshot: AuthSnapshot = { hasSession: false, isExpired: false, tokenExpiry: null, provider: null }
  const storage = getStorage()
  if (!storage) return snapshot
  try {
    for (const key of SUPABASE_AUTH_STORAGE_KEYS) {
      const raw = storage.getItem(key)
      if (!raw) continue
      const parsed = JSON.parse(raw)
      if (!parsed?.access_token) continue
      // First hit wins — sb-clanda-sl is the canonical SL session (listed
      // first above). Onboarding / app variants are fallback signals.
      snapshot.hasSession = true
      // expires_at is a UNIX-seconds epoch. isExpired is a derived BOOLEAN so
      // triage can tell "logged-out" from "stale token" without the token
      // itself ever leaving the device (no PII in the bundle). Null expiry →
      // not expired (we can't prove staleness, so don't assert it).
      const expiresAtSec: number | null = typeof parsed?.expires_at === 'number' ? parsed.expires_at : null
      snapshot.tokenExpiry = expiresAtSec ? new Date(expiresAtSec * 1000).toISOString() : null
      snapshot.isExpired = expiresAtSec ? expiresAtSec * 1000 < Date.now() : false
      const provider = parsed?.user?.app_metadata?.provider
      snapshot.provider = provider != null ? provider : null
      break
    }
  } catch (err) {
    // Honest partial capture (D-33 R1): the auth snapshot is a best-effort
    // diagnostic read — surface the capture failure loudly instead of
    // pretending the storage was cleanly empty, then return what we have.
    console.error('[debugReport] auth snapshot capture failed:', err)
  }
  return snapshot
}

export async function captureServiceWorkerSnapshot(): Promise<ServiceWorkerSnapshot> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration()
    const activeState = reg?.active?.state
    return { state: activeState != null ? activeState : null, waiting: !!reg?.waiting }
  } catch {
    return { state: null, waiting: false }
  }
}

export async function captureCallQueueSnapshot(
  recentActivity: DiagnosticData['recentActivity'],
): Promise<DiagnosticData['queueState']> {
  if (!recentActivity.find((a) => a.event === 'comms_session_started')) return null
  try {
    // Table renamed in raider-intel CD-01b cutover (2026-04-20):
    // prequal_call_queue → call_queue. Before this fix the bug-report
    // capture threw on every prod session with a started comms loop,
    // silently drop the queue-state block, AND left console_entries
    // empty because the throw fired before console capture finished.
    // allow-l7-boundary: best-effort diagnostic snapshot of the calling queue for the bug-report bundle; SL gateway for the debug-report sink is a tracked SL follow-on (scout_activity_log gateway FR)
    const { data: queueRows } = await supabase.from('call_queue').select('status')
    if (!queueRows) return null
    const rows = queueRows as Array<{ status: string }>
    return {
      pending: rows.filter((r) => r.status === 'pending').length,
      in_progress: rows.filter((r) => r.status === 'in_progress').length,
      total: rows.length,
    }
  } catch {
    return null
  }
}

export async function captureRecentActivity(userId: string | null): Promise<DiagnosticData['recentActivity']> {
  if (!userId) return []
  // Bearer-token helper, not the anon supabase client — the RLS that blocked
  // the insert also filtered the select to zero rows, silently emptying every
  // prior report. getRecentActivity now returns a typed result; NEVER-DARK: a
  // failed read is surfaced as a synthetic diagnostic entry rather than an empty
  // list, so a debug report shows WHY activity is missing instead of implying the
  // rep simply had none.
  const result = await getRecentActivity(userId, 30)
  if (result.ok) return result.activities
  return [{
    event: 'recent_activity_fetch_failed',
    payload: { error: result.error },
    created_at: new Date().toISOString(),
  }]
}

export function captureLocalStorageSnapshot(): Record<string, string | null> {
  const lsKeys = ['layout-preferences', 'prequal.widget-layout', 'prequal-onboarding-dismissed']
  const lsData: Record<string, string | null> = {}
  const storage = getStorage()
  if (!storage) {
    for (const k of lsKeys) lsData[k] = null
    return lsData
  }
  for (const k of lsKeys) {
    try { lsData[k] = storage.getItem(k) } catch { lsData[k] = null }
  }
  return lsData
}

export async function captureIdbQueue(): Promise<IdbQueueSummary | null> {
  try {
    const entries = await getQueueEntries()
    const pending = await getPendingCount()
    const oldestCreatedAt = entries[0]?.createdAt
    return {
      pending,
      conflict: entries.filter(e => e.status === 'conflict').length,
      failed: entries.filter(e => e.status === 'failed').length,
      oldest: oldestCreatedAt != null ? oldestCreatedAt : null,
      entries: entries.slice(0, 10).map(e => {
        const p = e.payload as { payload?: { unified_lead_id?: string } } | { unified_lead_id?: string } | undefined
        const inner = (p && typeof p === 'object' && 'payload' in p ? p.payload : p) as { unified_lead_id?: string } | undefined
        const leadId = inner?.unified_lead_id
        return { type: e.type, status: e.status, retries: e.retries, createdAt: e.createdAt, leadId: leadId != null ? leadId : null }
      }),
    }
  } catch (err) {
    // Honest degraded shape (D-33 R1): the IDB queue summary is a best-effort
    // diagnostic block — log the capture failure loudly; `null` in the bundle
    // means "queue unreadable", which triage can distinguish from an empty queue.
    console.error('[debugReport] IDB queue snapshot capture failed:', err)
    return null
  }
}

export function captureBrowserSnapshot(): DiagnosticData['browser'] {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    online: navigator.onLine,
    viewport: { w: window.innerWidth, h: window.innerHeight },
  }
}
